import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPinLabel } from './pin-label.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un condensador electrolítico radial visto
 * en perspectiva ligera desde arriba: el cilindro se dibuja como
 *
 *   ┌──── elipse plateada (cara superior circular vista de canto)
 *   │
 *   │──── cuerpo cilíndrico (rectángulo con degradado)
 *   │
 *   └──── curva inferior (arco: cara inferior oculta detrás del
 *           cuerpo, sólo se intuye el borde curvado del fondo)
 *
 * La franja lateral del cátodo y las marcas "−" van sobre la pared
 * cilíndrica. Etiquetas +/− junto a los pines.
 */
export function renderElectrolyticBody(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const width = def.body.length_mm ?? 5.08;
  const height = def.body.width_mm ?? 10.16;
  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.28);
  const veryLight = mix(bodyColor, '#ffffff', 0.55);
  const bevelOuter = mix(bodyColor, '#ffffff', 0.12);
  const metalColor = def.appearance.metal_color ?? '#c8c8c8';
  // Contorno con el color del cuerpo un poco más oscuro (no negro),
  // para que la silueta se integre con el azul del condensador.
  const outlineColor = mix(bodyColor, '#000000', 0.6);
  const outlineWidth = 0.1;

  const hL = width / 2;
  const hW = height / 2;
  // Radio VERTICAL de la elipse superior (la horizontal ya es hL).
  // Se puede aplanar bajando body.corner_radius_mm.
  const capRy = Math.min(def.body.corner_radius_mm ?? width * 0.30, hL);
  // Curva inferior: un poco más plana que la tapa (perspectiva).
  const bottomRy = capRy * 0.75;

  // Extensión visual del cuerpo por debajo del borde nominal (hW):
  // así los pines quedan tapados en su primer tramo por la falda
  // del cilindro y no aparecen despegados del cuerpo.
  const skirtDown = Math.max(0.5, width * 0.15);
  const effHW = hW + skirtDown;

  // Centros verticales de las dos elipses.
  const topCy = -hW + capRy;
  const botCy = effHW - bottomRy;

  const drawOuterPath = (ctx: any) => {
    // Contorno del cilindro visto en perspectiva:
    //   - mitad superior de la elipse superior (curva por arriba)
    //   - lateral derecho recto
    //   - mitad inferior de la elipse inferior (curva por abajo)
    //   - lateral izquierdo recto
    ctx.beginPath();
    ctx.moveTo(-hL, topCy);
    ctx.ellipse(0, topCy, hL, capRy, 0, Math.PI, 2 * Math.PI, false);
    ctx.lineTo(hL, botCy);
    ctx.ellipse(0, botCy, hL, bottomRy, 0, 0, Math.PI, false);
    ctx.lineTo(-hL, topCy);
    ctx.closePath();
  };

  const bodyGroup = new Konva.Group({
    clipFunc: (ctx: any) => drawOuterPath(ctx),
  });

  // 1) Pared cilíndrica: rellena TODA la silueta con degradado
  //    vertical (arriba oscuro → abajo claro). Se ve entre la
  //    tapa superior y la curva inferior.
  // Al invertir el componente el grupo entero lleva scaleY(-1), así que
  // la lata sigue leyéndose como un cilindro: lo único que cambia es
  // que lo vemos por el otro lado. Por eso se conserva TODO el estilo
  // (degradado, tapa metálica, franja de cátodo) y sólo se ESPEJA en X
  // lo que depende del lado: el sentido del degradado, la posición de
  // la franja y la del brillo especular.
  const sx = flipped ? -1 : 1;
  const bodyShape = new Konva.Shape({
    sceneFunc: (ctx, shape) => {
      drawOuterPath(ctx);
      ctx.fillStrokeShape(shape);
    },
    // Gradiente HORIZONTAL: claro a la izquierda → oscuro a la derecha,
    // dando la sensación cilíndrica con la luz entrando por un lado.
    // Invertido, el degradado se espeja para que la luz siga viniendo
    // del mismo lado en coordenadas de pantalla.
    fillLinearGradientStartPoint: { x: -hL * sx, y: 0 },
    fillLinearGradientEndPoint: { x: hL * sx, y: 0 },
    fillLinearGradientColorStops: [
      0, veryLight,
      0.5, light,
      1, bevelOuter,
    ],
    listening: true,
  });
  bodyShape.setAttr('nodeKind', 'body');
  bodyGroup.add(bodyShape);

  {
    // 2) Franja lateral del cátodo (banda blanca semiopaca con "−").
    //    Va sobre la pared, DEBAJO de la tapa superior: el usuario
    //    percibe que la banda arranca al pie de la tapa. Invertida,
    //    la franja pasa al lado opuesto (vemos la lata por detrás).
    const stripeW = width * 0.28;
    const stripeX = flipped ? -hL : hL - stripeW;
    const stripeTotalH = effHW + hW;
    bodyGroup.add(
      new Konva.Rect({
        x: stripeX,
        y: -hW,
        width: stripeW,
        height: stripeTotalH,
        fill: '#e8e8ff',
        opacity: 0.85,
        listening: false,
      }),
    );
    const nMinus = Math.max(3, Math.round(stripeTotalH / 3));
    const fontMinus = stripeW * 0.9;
    const spacing = stripeTotalH / nMinus;
    for (let i = 0; i < nMinus; i++) {
      const cy = -hW + spacing * (i + 0.5);
      bodyGroup.add(
        new Konva.Text({
          x: stripeX,
          y: cy - fontMinus / 2,
          width: stripeW,
          height: fontMinus,
          text: '−',
          fontSize: fontMinus,
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          fontStyle: 'bold',
          fill: '#0a1e5c',
          align: 'center',
          verticalAlign: 'middle',
          listening: false,
        }),
      );
    }

    // 3) Tapa superior circular vista de canto → ELIPSE PLATEADA
    //    con degradado LINEAL (borde oscuro → centro claro → borde
    //    oscuro) para simular el reflejo del metal pulido en la
    //    circunferencia superior de la lata.
    bodyGroup.add(
      new Konva.Ellipse({
        x: 0,
        y: topCy,
        radiusX: hL,
        radiusY: capRy,
        fillLinearGradientStartPoint: { x: -hL * sx, y: topCy },
        fillLinearGradientEndPoint: { x: hL * sx, y: topCy },
        fillLinearGradientColorStops: [
          0, mix(metalColor, '#000000', 0.45),
          0.18, mix(metalColor, '#000000', 0.15),
          0.5, mix(metalColor, '#ffffff', 0.55),
          0.82, mix(metalColor, '#000000', 0.15),
          1, mix(metalColor, '#000000', 0.45),
        ],
        stroke: outlineColor,
        strokeWidth: outlineWidth,
        listening: false,
      }),
    );
    // Brillo alargado en la ZONA SUPERIOR IZQUIERDA de la tapa,
    // orientado en diagonal (-45°). Simula el reflejo especular
    // de una fuente de luz situada arriba-a-la-izquierda. Al invertir
    // se espeja en X (y con él el ángulo de la diagonal).
    bodyGroup.add(
      new Konva.Ellipse({
        x: -hL * 0.35 * sx,
        y: topCy - capRy * 0.25,
        radiusX: hL * 0.55,
        radiusY: capRy * 0.18,
        rotation: -45 * sx,
        fill: '#ffffff',
        opacity: 0.55,
        listening: false,
      }),
    );
  }

  g.add(bodyGroup);

  // Contorno perimetral (fuera del clip, siempre nítido).
  g.add(new Konva.Shape({
    sceneFunc: (ctx, shape) => {
      drawOuterPath(ctx);
      ctx.fillStrokeShape(shape);
    },
    stroke: outlineColor,
    strokeWidth: outlineWidth,
    listening: false,
  }));

  // Etiqueta central con el valor, contra-transformada.
  const target = Math.abs(rotationDeg % 180) === 0 ? 0 : 90;
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    // Un poco por debajo del centro para no chocar con la tapa.
    y: capRy * 0.5,
    rotation: ps * (target - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? Math.min(width, height) * 0.22;
  const boxSize = Math.max(width, height) * 0.7;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, boxSize);
  g.add(labelGroup);

  // Etiquetas + y − junto a cada pin, fuera del cuerpo cerca de éste.
  const labelFont = Math.min(width * 0.4, 1.4);
  const pinLabels: Record<string, string> = { '1': '+', '2': '−' };
  for (const pin of def.pins) {
    const t = pinLabels[pin.id] ?? '';
    if (t) {
      g.add(renderPinLabel(pin, t, 1.4, rotationDeg, flipped, labelFont));
    }
  }

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
